<script>
import { mapActions } from 'vuex'

export default {
  name: 'ToolsList',
  components: {
    Action: () => import('@/components/general/Action'),
    Dropdown: () => import('@/components/general/Dropdown'),
    Datatable: () => import('@/components/general/Datatable'),
    FilterList: () => import('@/components/general/FilterList'),
    Pagination: () => import('@/components/general/Pagination'),
    DropdownLink: () => import('@/components/general/DropdownLink'),
    ContentHeader: () => import('@/components/general/ContentHeader'),
    FilterListSearch: () => import('@/components/general/FilterListSearch'),
    Tabs: () => import('@/components/general/Tabs'),
    PageLimitSelector: () => import('@/components/general/PageLimitSelector')
  },

  data () {
    return {
      pagination: {
        query: {},
        filter: {},
        page: 1,
        limit: 12,
        lastPage: null,
        order: {
          active: 'desc',
          title: 'asc'
        }
      },

      tabLinks: [
        {
          location: { name: 'tools.index' },
          text: 'Ferramentas'
        },
        {
          location: { name: 'tools.type_tools.index' },
          text: 'Tipos de Ferramentas'
        }
      ],

      toolId: null
    }
  },

  computed: {
    toolsList () {
      return this.$store.getters.toolsList
    },

    canWrite () {
      return this.getContextPermission('tools') === 'write'
    }
  },

  watch: {
    'pagination.page' () {
      this.getToolsList()
    }
  },

  created () {
    this.getToolsList()
  },

  methods: {
    ...mapActions([
      'setFetching',
      'setFeedback',
      'attemptToggleToolActive',
      'attemptGetToolsList'
    ]),

    prevPage () {
      if (this.pagination.page > 1) {
        this.pagination.page -= 1
      }
    },

    nextPage () {
      this.pagination.page += 1
    },

    firstPage () {
      this.pagination.page = 1
    },

    lastPage () {
      this.pagination.page = this.pagination.lastPage
    },

    getToolsList () {
      this.setFetching(true)

      this.attemptGetToolsList(this.pagination).then(({ data }) => {
        this.pagination.lastPage = data.lastPage
      }).finally(() => {
        this.setFetching(false)
      })
    },

    onLimitChange (newLimit) {
      this.pagination.limit = newLimit

      if (this.pagination.page === 1) {
        this.getToolsList()
      } else {
        this.pagination.page = 1
      }
    },

    createNewTool () {
      this.$router.push({ name: 'tools.manage' })
    },

    searchToolsPage (search) {
      this.pagination.query = {
        title: search
      }

      if (this.pagination.page === 1) {
        this.getToolsList()
      } else {
        this.pagination.page = 1
      }
    },

    edit (toolId, tab = 1) {
      this.$router.push({
        name: 'tools.manage',
        params: {
          id: toolId,
          tab
        }
      })
    },

    toggleActive (tool) {
      this.setFetching(true)

      this.attemptToggleToolActive(tool.id).then((response) => {
        this.setFeedback({
          message: response.data.active
            ? this.$t('tool:feedback.active.success')
            : this.$t('tool:feedback.deactivate.success')
        })

        this.getToolsList()
      }).catch(({ data }) => {
        if (data?.error?.validation?.objectives === 'invalid_objectives_array_required') {
          this.setFeedback({ message: this.$t('tool:feedback.deactivate.error.objectives.inactives') })

          this.$router.push({
            name: 'tools.manage',
            params: { id: tool.id }
          })
        } else {
          this.setFeedback({ message: this.$t('global.error') })
        }
      }).finally(() => {
        this.setFetching(false)
      })
    },

    openPage (tool) {
      window.open(`${process.env.VUE_APP_PORTAL_URL}ferramenta/${tool.slug}`, '_blank')
    },

    openExternalLinkPage (track) {
      window.open(`${process.env.VUE_APP_PORTAL_URL}ferramenta/${track.slug}?isExternalLink=true`, '_blank')
    }
  }
}
</script>

<template>
  <div class="main-content">
    <ContentHeader
      :title="$t('tools:header.title')"
      :description="$t('tools:header.description')"
      background="/assets/images/themes/default/png/partners_banner.png"
    >
      <Tabs
        slot="tabs"
        dark
        :links="tabLinks"
      />
    </ContentHeader>
    <FilterList>
      <Action
        v-if="canWrite"
        slot="button"
        primary
        large
        fixed-width
        type="button"
        :text="$t('tool.list:btn.add')"
        @click="createNewTool"
      />
      <PageLimitSelector
        slot="order"
        :value="pagination.limit"
        storage-key="page_limit_tools"
        @input="onLimitChange"
      />
      <FilterListSearch
        slot="search"
        :hide-error-details="true"
        @search="searchToolsPage"
      />
    </FilterList>
    <div
      v-if="toolsList.length"
      class="center thematic-pages-table"
    >
      <Datatable :items="toolsList">
        <template slot="thead">
          <tr>
            <th class="th">
              {{ $t('tools:datatable.header.1') }}
            </th>
            <th
              class="th"
              width="200"
            >
              {{ $t('tools:datatable.header.2') }}
            </th>
            <th class="th text-center">
              {{ $t('solutions.list:datatable.header.6') }}
            </th>

            <th class="th text-center">
              {{ $t('solutions.list:datatable.header.7') }}
            </th>

            <th
              class="th text-center"
              width="80"
            >
              {{ $t('solutions.list:datatable.header.4') }}
            </th>

            <th
              class="th"
              width="130"
            >
              {{ $t('global:datatable.audit.created-at') }}
            </th>
            <th
              class="th"
              width="180"
            >
              {{ $t('global:datatable.audit.updated-at') }}
            </th>
            <th
              class="th"
              width="40"
            />
          </tr>
        </template>
        <template
          slot="tbody"
          slot-scope="props"
        >
          <tr>
            <td class="td">
              <span class="td-text bolder">{{ props.item.title }}</span>
            </td>
            <td class="td">
              <span class="td-text bolder">{{ props.item.toolType.title }}</span>
            </td>
            <td class="td text-center">
              <span
                :class="{ 'td-tag active-ribbon': props.item.showOnPortal, 'td-tag inactive-ribbon': !props.item.showOnPortal }"
              >
                {{ Boolean(props.item.showOnPortal) ? $t('global:yes') : $t('global:no') }}</span>
            </td>

            <td class="td text-center">
              <span
                :class="{ 'td-tag active-ribbon': props.item.isExternalLink, 'td-tag inactive-ribbon': !props.item.isExternalLink }"
              >
                {{ Boolean(props.item.isExternalLink) ? $t('global:yes') : $t('global:no') }}</span>
            </td>

            <td class="td">
              <span
                v-if="!!props.item.active"
                class="td-tag active-ribbon"
              >{{ $t('global.status:active') }}</span>
              <span
                v-else
                class="td-tag inactive-ribbon"
              >{{ $t('global.status:inactive') }}</span>
            </td>
            <td class="td">
              <span class="td-text">{{ formatDate(props.item.createdAt) || '—' }}</span>
            </td>
            <td class="td">
              <span class="td-text td-block">{{ formatDate(props.item.userUpdatedAt || props.item.createdAt) || '—' }}</span>
              <span
                v-if="props.item.userUpdatedAt || props.item.createdAt"
                class="td-text td-audit-sub"
              >{{ formatTime(props.item.userUpdatedAt || props.item.createdAt) }}h{{ auditAuthorLabel(props.item) }}</span>
            </td>
            <td
              class="td td-actions"
              width="40"
            >
              <Dropdown
                icon="dots-vertical"
                right
              >
                <DropdownLink
                  v-if="canWrite"
                  :text="$t('global:edit')"
                  @click="edit(props.item.id,1)"
                />
                <DropdownLink
                  v-if="canWrite"
                  :text="$t('global:qualify')"
                  @click="edit(props.item.id, 2)"
                />
                <DropdownLink
                  v-if="canWrite"
                  :text="$t('global:integrations')"
                  @click="edit(props.item.id, 3)"
                />
                <DropdownLink
                  v-if="canWrite"
                  :text="$t('global:seo')"
                  @click="edit(props.item.id, 4)"
                />
                <DropdownLink
                  v-if="canWrite"
                  :text="$t('global:links')"
                  @click="edit(props.item.id, 5)"
                />
                <DropdownLink
                  v-if="props.item.showOnPortal"
                  :text="$t('global:show.page')"
                  @click="openPage(props.item)"
                />
                <DropdownLink
                  v-if="props.item.isExternalLink"
                  :text="$t('global:show.external.link')"
                  @click="openExternalLinkPage(props.item)"
                />
                <DropdownLink
                  v-if="!props.item.active && canWrite"
                  :text="$t('global:activate')"
                  @click="toggleActive(props.item)"
                />
                <DropdownLink
                  v-else-if="canWrite"
                  :text="$t('global:deactivate')"
                  @click="toggleActive(props.item)"
                />
              </Dropdown>
            </td>
          </tr>
        </template>
      </Datatable>
      <Pagination
        :active-page="pagination.page"
        :page-count="pagination.lastPage"
        @firstPage="firstPage"
        @lastPage="lastPage"
        @nextPage="nextPage"
        @previousPage="prevPage"
      />
    </div>
    <div
      v-else
      class="center thematic-pages-table"
    >
      <p>Nenhuma ferramenta encontrada para o termo pesquisado</p>
    </div>
  </div>
</template>
